/** A short two-note chime from Web Audio: no sound file to ship, and nothing plays before a gesture. */
export function playChime(): void {
  const audio = window as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const AudioContextClass = audio.AudioContext ?? audio.webkitAudioContext;
  if (!AudioContextClass) return;
  try {
    const context = new AudioContextClass();
    const gain = context.createGain();
    gain.connect(context.destination);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.5);
    [880, 1320].forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      oscillator.connect(gain);
      oscillator.start(context.currentTime + index * 0.12);
      oscillator.stop(context.currentTime + 0.5);
    });
    setTimeout(() => void context.close().catch(() => undefined), 800);
  } catch {
    // Autoplay refused or audio unavailable: the visual alert still shows.
  }
}

export function desktopNotificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/** Asks once; true when the browser lets us show notifications. */
export async function requestDesktopPermission(): Promise<boolean> {
  if (!desktopNotificationsSupported()) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  return (await Notification.requestPermission()) === 'granted';
}

/** Shown only while the tab is in the background; clicking focuses the tab and opens the thread. */
export function showDesktopNotification(title: string, body: string, onClick: () => void): void {
  if (!desktopNotificationsSupported() || Notification.permission !== 'granted') return;
  if (document.visibilityState === 'visible') return;
  try {
    const notification = new Notification(title, { body, tag: 'nucleus-inbox' });
    notification.onclick = () => {
      window.focus();
      onClick();
      notification.close();
    };
  } catch {
    // Some browsers only allow notifications from a service worker; the sound still plays.
  }
}
